/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { forkJoin, Subscription } from 'rxjs';
import { AbstractObject } from '@src/app/model/abstract-object';
import { AiLog } from '@src/app/model/ai-log';
import { Area } from '@src/app/model/area';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { SrClass } from '@src/app/model/sr-class';
import { SrTopic } from '@src/app/model/sr-topic';
import { AiProposalAnswer, RequestService } from '@src/app/modules/crm/request.service';
import { SrClassService } from '@src/app/modules/crm/sr-class.service';
import { SrTopicService } from '@src/app/modules/crm/sr-topic.service';
import { AreaService } from '@src/app/modules/unit/area.service';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { DialogBannerComponent } from '@src/app/shared/dialogs/dialog-banner-component';
import { FieldSet, FieldSetComponent } from '@src/app/shared/form/field-set/field-set.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormRichTextComponent } from '@src/app/shared/form/form-rich-text/form-rich-text.component';
import { IsNarrowService } from '@src/app/shared/is-narrow.service';
import { MessageService } from '@src/app/shared/message.service';
import { required } from '@src/app/shared/validators';

export interface AiProposalData {
    sr: ServiceRequest;
    log: AiLog;
    canSend: boolean;
}

/**
 * The AI's answer, put to an agency user before the reporter ever sees it. Everything on the form is
 * the assistant's suggestion and every part of it can be overruled; what is saved is compared with
 * what was proposed on the server, so the difference between "that will do" and "I rewrote it" is
 * recorded without anyone having to say which it was.
 *
 * Nothing here decides whether to ask - the page does that, once, when it opens.
 */
@Component({
    selector: 'app-ai-proposal',
    imports: [MatDialogModule, MatButtonModule, FieldSetComponent, DialogBannerComponent],
    templateUrl: './ai-proposal.component.html',
    styleUrl: './ai-proposal.component.scss'
})
export class AiProposalComponent implements OnDestroy {
    dialogRef = inject(MatDialogRef<AiProposalComponent>);
    data = inject<AiProposalData>(MAT_DIALOG_DATA);
    private srSvc = inject(RequestService);
    private topicSvc = inject(SrTopicService);
    private classSvc = inject(SrClassService);
    private areaSvc = inject(AreaService);
    private msgSvc = inject(MessageService);
    private cds = inject(ConfirmDialogService);
    private screenSvc = inject(IsNarrowService);

    sr = this.data.sr;
    log = this.data.log;
    canSend = this.data.canSend;
    ready = false;
    saving = false;
    isPhone = false;
    private subscriptions: Subscription[] = [];

    private classes: SrClass[] = [];
    private areas: Area[] = [];

    fieldSet = new FieldSet({ formLayout: this.canSend
        ? [{ cells: [{ width: '40%' }, { width: '60%' }] }]
        : [{ cells: [{}] }] });

    topicClass = ServiceRequest.makeTopicAndClassFields(() => this.classes);
    areaCore = ServiceRequest.makeAreaAndCoreFields(() => this.areas, 'srArea', 'srCore');

    statusField = FormPicklistComponent.make($localize`Then what?`, 'srStatus', null, {
        items: [
            { id: 'open', name: $localize`Leave it open for the team` },
            { id: 'closed', name: $localize`This answers it - close the request` },
        ],
        allowSelectNone: false,
    }, { validators: [required] });

    commentField = FormRichTextComponent.make($localize`Reply to the person who raised it`,
        'comment', { formColumn: 2, validators: [required] });

    fields = [
        this.topicClass.topicField.override({ formColumn: 1 }),
        this.topicClass.classField.override({ formColumn: 1 }),
        this.areaCore.areaField.override({ formColumn: 1 }),
        this.areaCore.coreField.override({ formColumn: 1 }),
        this.statusField.override({ formColumn: 1 }),
        this.commentField,
    ];

    constructor() {
        this.subscriptions.push(this.screenSvc.detectVeryNarrow().subscribe(r => { this.isPhone = r }));
        if (!this.canSend) {
            this.fields = this.fields.filter(f => f !== this.commentField);
        }
        this.fieldSet.setFields(this.fields);
        this.loadReferenceData();
    }

    get proposed() {
        return this.log?.proposed ?? {};
    }

    /** data-form-ready only goes up once every picklist has its list - see client/CLAUDE.md. */
    private loadReferenceData() {
        forkJoin({
            topics: this.topicSvc.get<SrTopic>(false, null, this.sr.teamId),
            classes: this.classSvc.get<SrClass>(false, null, this.sr.teamId),
            areas: this.areaSvc.get<Area>(false, null, this.sr.teamId),
        }).subscribe(r => {
            this.classes = r.classes;
            this.areas = r.areas;
            this.topicClass.topicField.setPicklistItems(r.topics);
            this.areaCore.areaField.setPicklistItems(r.areas);
            this.seedAnswer();
            this.ready = true;
        });
    }

    /**
     * The assistant's answer is the starting point for every field, including the ones it left
     * blank. An area it could not work out comes back as an empty string - see buildSchema in
     * xeroServer - which a picklist wants as a null.
     */
    private seedAnswer() {
        const answer = this.proposed;
        const area = answer.srArea || null;
        this.topicClass.narrowClasses(answer.srTopic);
        this.areaCore.narrowCores(area);
        this.fieldSet.setValue({
            srTopic: answer.srTopic,
            srClass: answer.srClass,
            srArea: area,
            srCore: answer.srCore || null,
            srStatus: answer.srStatus ?? 'open',
            comment: this.log?.commentHtml ?? '',
        } as unknown as AbstractObject, false);
    }

    highlightErrors() {
        this.fields.forEach(f => {
            if (!f.control?.valid) {
                f.control.markAsTouched();
            }
        });
    }

    /** The reply is not needed to save without sending, so only the other fields have to be valid. */
    get classificationValid(): boolean {
        return this.fields.filter(f => f !== this.commentField).every(f => f.control?.valid);
    }

    private reviewedAnswer(): AiProposalAnswer {
        return { ...this.fieldSet.getFormValue(), aiLogId: this.log.id } as unknown as AiProposalAnswer;
    }

    send() {
        this.saving = true;
        this.srSvc.acceptAiProposal(this.sr, this.reviewedAnswer()).subscribe({
            next: updated => this.dialogRef.close(updated),
            error: err => {
                this.saving = false;
                this.msgSvc.showError(err, 'Sending the answer');
            }
        });
    }

    saveWithoutSending() {
        const title = $localize`Save without sending?`;
        const message = $localize`The topic, class and area are saved, but no reply is sent to the
            person who raised the request. If you chose to close it, it is closed as No Action.`;
        this.cds.open(title, message, () => {
            this.saving = true;
            this.srSvc.saveAiProposalWithoutSending(this.sr, this.reviewedAnswer()).subscribe({
                next: updated => this.dialogRef.close(updated),
                error: err => {
                    this.saving = false;
                    this.msgSvc.showError(err, 'Saving the answer');
                }
            });
        }, $localize`Save`);
    }

    /**
     * Thrown away rather than left: cancelling leaves the answer waiting and it is offered again the
     * next time the request is opened, which is what someone who wants to think about it should get.
     */
    reject() {
        const title = $localize`Reject this answer?`;
        const message = $localize`Nothing will be sent to the person who raised the request, and the
            assistant will not be asked about it again.`;
        this.cds.open(title, message, () => {
            this.saving = true;
            this.srSvc.rejectAiProposal(this.sr, this.log.id).subscribe(updated => {
                this.dialogRef.close(updated);
            });
        }, $localize`Reject`);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.subscriptions = [];
    }
}
